<template>
  <BaseDialog :isOpen="isOpen" :sending="sending" :confirmText="confirmText"
    :closeText="closeText" submitType="submit" :formId="formId" actionsClass="mt-5"
    @close="$emit('close')" @confirm="$emit('submit')">

    <!-- User identity -->
    <div class="flex flex-col items-center mb-5">
      <UserAvatar v-if="userName && accentColor" :name="userName" :color="accentColor" class="mb-3" />
      <DialogTitle as="h3"
        class="text-[17px] font-bold text-foreground tracking-tight text-center">
        {{ title }}
      </DialogTitle>
    </div>

    <!-- Password form -->
    <form :id="formId" @submit.prevent="$emit('submit')" :name="name">
      <!-- Password input -->
      <div class="relative">
        <div class="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
          <LockClosedIcon class="h-[16px] w-[16px] pwd-lock-icon" />
        </div>
        <input :value="modelValue" @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
          type="password" required autocomplete="current-password"
          :id="name" :name="name"
          :placeholder="placeholder"
          class="w-full h-[48px] pl-10 pr-4 text-[14px] font-medium rounded-[14px] pwd-input text-foreground placeholder:text-muted-foreground outline-none transition-all duration-200"
          :class="{ 'pwd-input-error': error }" />
      </div>

      <!-- Error message -->
      <Transition enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-1" enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 -translate-y-1">
        <p v-if="error" class="mt-2 ml-1 text-[12px] font-medium" style="color: #E3211C;">
          {{ error }}
        </p>
      </Transition>
    </form>

  </BaseDialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { LockClosedIcon } from '@heroicons/vue/24/outline';
import { DialogTitle } from '@headlessui/vue';
import BaseDialog from './BaseDialog.vue';
import UserAvatar from '../UserAvatar.vue';

const props = defineProps<{
  isOpen: boolean;
  title: string;
  userName?: string;
  accentColor?: string;
  modelValue: string;
  placeholder: string;
  error: string;
  sending: boolean;
  confirmText: string;
  closeText: string;
  name?: string;
}>();

defineEmits<{
  (e: 'close'): void;
  (e: 'submit'): void;
  (e: 'update:modelValue', value: string): void;
}>();

const formId = computed(() => `pwd-form-${props.name || 'default'}`);
</script>

<style scoped>
/* Light mode */
.pwd-lock-icon {
  color: #35AAD5;
}
.pwd-input {
  background: rgba(6, 200, 255, 0.03);
  border: 1.5px solid rgba(0, 0, 0, 0.07);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.03);
}
.pwd-input:focus {
  border-color: #06C8FF;
  box-shadow: 0 0 0 3px rgba(6, 200, 255, 0.10), inset 0 1px 2px rgba(0, 0, 0, 0.02);
  background: rgba(6, 200, 255, 0.02);
}
.pwd-input-error {
  border-color: #E3211C;
}
.pwd-input-error:focus {
  border-color: #E3211C;
  box-shadow: 0 0 0 3px rgba(227, 33, 28, 0.10);
}

/* Dark mode */
:is(.dark *).pwd-lock-icon {
  color: rgba(6, 200, 255, 0.5);
}
:is(.dark *).pwd-input {
  background: rgba(255, 255, 255, 0.05);
  border: 1.5px solid rgba(6, 200, 255, 0.08);
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.2);
}
:is(.dark *).pwd-input:focus {
  border-color: #06C8FF;
  box-shadow: 0 0 0 3px rgba(6, 200, 255, 0.15), inset 0 1px 3px rgba(0, 0, 0, 0.15);
  background: rgba(255, 255, 255, 0.07);
}
:is(.dark *).pwd-input-error {
  border-color: #E3211C;
}
:is(.dark *).pwd-input-error:focus {
  border-color: #E3211C;
  box-shadow: 0 0 0 3px rgba(227, 33, 28, 0.15);
}
</style>
